
import Vue from "vue";
import { Component, Prop, Watch } from "vue-property-decorator";
import defensiveCopy from "@/util/defensive-copy";
import { getVariableTemplates } from "@/oxygen/variables/lib/utils";
import { Variable } from "@/oxygen/variables/lib/types";
import VariableRepeaterItem from "@/oxygen/variables/VariableRepeaterItem.vue";
import Icon from "@/components/UI/Icon.vue";
import draggable from "vuedraggable";
import IconButton from "@/components/UI/IconButton.vue";
import { globalStore } from "@/store/store-accessor";
import CollectionsInput from "@/oxygen/CollectionsInput.vue";
import { breakdanceUndo } from "@/lib/undo/undo";

@Component({
  components: {
    Icon,
    IconButton,
    draggable,
    CollectionsInput,
    VariableRepeaterItem
  }
})
export default class GlobalVariablesInput extends Vue {
  @Prop({
    default() {
      return [];
    }
  })
  value!: Variable[];

  variablesByCollection: Record<string, Variable[]> = {};

  @Watch("value", {
    immediate: true,
    deep: true
  })
  onValueChange() {
    this.updateInternalValue();
    this.checkMissingCollections();
  }

  @Watch("collections", {
    immediate: true,
    deep: true
  })
  onColsChange() {
    this.updateInternalValue();
  }

  get collections() {
    return globalStore.variablesCollections;
  }

  get labels() {
    return this.value.map(item => item.label);
  }

  get control() {
    return {
      options: {
        repeaterOptions: {
          buttonName: `Add Variable`,
          templates: getVariableTemplates(this.labels)
        }
      }
    };
  }

  checkMissingCollections() {
    const currentCollections = [
      ...new Set(this.value.map(item => item.collection))
    ];

    const missingCollections = currentCollections.filter(
      item => !this.collections.includes(item)
    );

    if (missingCollections.length) {
      globalStore.setVariablesCollections(currentCollections);
    }
  }

  beforeCreate() {
    // @ts-ignore
    this.$options.components.InlineRepeater =
      require("@/components/Reusable/CustomInputs/InlineRepeater.vue").default;
  }

  updateInternalValue() {
    this.collections.forEach(collection => {
      this.$set(
        this.variablesByCollection,
        collection,
        this.getCollectionVariables(collection)
      );
    });
  }

  getCollectionControls(collection: string) {
    const templates = getVariableTemplates(this.labels, collection);

    const updatedRepeaterOptions = {
      ...this.control.options?.repeaterOptions,
      templates
    };

    const updatedOptions = {
      ...this.control.options,
      repeaterOptions: updatedRepeaterOptions
    };

    return {
      ...this.control,
      options: updatedOptions
    };
  }

  getCollectionVariables(collection: string) {
    return this.value.filter(item => item.collection === collection) || [];
  }

  setVariable(variable: Variable) {
    const newVariables = defensiveCopy(this.value);

    const foundIndex = newVariables.findIndex(item => item.id === variable.id);

    if (foundIndex !== -1) {
      newVariables[foundIndex] = variable;
    }

    this.setVariables(newVariables);
  }

  setVariables(newVariables: Variable[]) {
    this.$emit("input", newVariables);
  }

  handleDrag(collectionVariables: Variable[], collection: string) {
    this.variablesByCollection[collection] = collectionVariables.map(v => ({
      ...v,
      collection
    }));

    const updatedVariables: Variable[] = [];

    this.collections.forEach(collection => {
      updatedVariables.push(...this.variablesByCollection[collection]);
    });

    this.setVariables(updatedVariables);
  }

  setCollections(collections: string[]) {
    breakdanceUndo.transaction(() => {
      globalStore.setVariablesCollections(collections);
    }, "Set Variable Collections");
  }
}
